import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Users } from 'lucide-react';
import ProgressResetTool from './ProgressResetTool';
import ResetHistoryList from './ResetHistoryList';

/**
 * ProgressResetPanel - Settings entry point: pick a course builder and course, then reset progress by department
 */
const ProgressResetPanel = ({ api }) => {
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');

  useEffect(() => {
    api.get('/progress-reset/sources')
      .then(res => setSources(res.data.sources || []))
      .catch(() => setSources([]));
  }, [api]);

  useEffect(() => {
    setCourseId('');
    setCourses([]);
    if (!source) return;
    api.get(`/progress-reset/${source}/courses`)
      .then(res => setCourses(res.data.courses || []))
      .catch(() => setCourses([]));
  }, [api, source]);

  return (
    <Card data-testid="progress-reset-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Reset User Progress by Department
        </CardTitle>
        <CardDescription>
          Choose a course builder and course, pick a department and users, then reset the whole course or specific modules / sub-modules
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Course builder</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="reset-source-select">
                <SelectValue placeholder="Select a course builder" />
              </SelectTrigger>
              <SelectContent>
                {sources.map(s => (
                  <SelectItem key={s.key} value={s.key} data-testid={`reset-source-option-${s.key}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Course</Label>
            <Select value={courseId} onValueChange={setCourseId} disabled={!source}>
              <SelectTrigger data-testid="reset-course-select">
                <SelectValue placeholder={source && !courses.length ? 'No courses in this builder' : 'Select a course'} />
              </SelectTrigger>
              <SelectContent>
                {courses.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`reset-course-option-${c.id}`}>
                    {c.title}{c.status !== 'published' ? ` (${c.status})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {source && courseId ? (
          <ProgressResetTool key={`${source}-${courseId}`} api={api} source={source} courseId={courseId} />
        ) : (
          <ResetHistoryList api={api} source={source || undefined} showCourse refreshKey={0} />
        )}
      </CardContent>
    </Card>
  );
};

export default ProgressResetPanel;
